import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, db, auth } from '../firebase';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Crown, 
  Gauge, 
  Users, 
  Building2, 
  Box, 
  Check, 
  X, 
  ShieldCheck, 
  ArrowRight, 
  ExternalLink, 
  Lock, 
  LogIn, 
  AlertCircle,
  RefreshCw,
  Sparkles
} from 'lucide-react';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  userType?: 'individual' | 'company';
  userEmail?: string;
  userId?: string;
}

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1 },
  { code: 'UGX', symbol: 'UGX ', name: 'Ugandan Shilling', rate: 3800 },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', rate: 1450 },
  { code: 'KES', symbol: 'KSh ', name: 'Kenyan Shilling', rate: 130 }
];

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, userEmail, userId }) => {
  const { user, userProfile, setUserProfile, setIsSignInModalOpen, setIsSignUpModalOpen } = useAuth();
  const [activeTab, setActiveTab] = useState<'individual' | 'corporate'>('individual');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [selectedCurrency, setSelectedCurrency] = useState<'USD' | 'UGX' | 'NGN' | 'KES'>('USD');
  const [checkoutPlan, setCheckoutPlan] = useState<any | null>(null);
  
  // Checkout & submission states
  const [checkoutStep, setCheckoutStep] = useState<'idle' | 'creating-session' | 'redirecting' | 'awaiting-completion'>('idle');
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Enterprise SLA form states
  const [companyName, setCompanyName] = useState('');
  const [seatsNeeded, setSeatsNeeded] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [enterpriseStep, setEnterpriseStep] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [enterpriseBundle, setEnterpriseBundle] = useState<any | null>(null);

  useEffect(() => {
    // Sync with user's preferred currency if available
    if (userProfile?.preferredCurrency && ['USD', 'UGX', 'NGN', 'KES'].includes(userProfile.preferredCurrency)) {
      setSelectedCurrency(userProfile.preferredCurrency);
    }
  }, [userProfile]);

  if (!isOpen) return null;

  const effectiveUserId = user?.uid || userId;
  const effectiveEmail = user?.email || userEmail || userProfile?.email;
  const isAuthenticated = Boolean(effectiveUserId && !user?.isAnonymous);

  // Pricing helper
  const getDisplayPrice = (usdMonthly: number, usdAnnual: number, hasAnnual: boolean = true) => {
    const isAnnual = billingCycle === 'annual' && hasAnnual;
    const usdAmount = isAnnual ? usdAnnual : usdMonthly;
    
    if (selectedCurrency === 'USD') {
      return { 
        priceStr: `$${usdAmount}/${isAnnual ? 'yr' : 'mo'}`, 
        amount: usdAmount, 
        note: isAnnual ? 'Paid annually' : 'Paid monthly' 
      };
    } else if (selectedCurrency === 'UGX') {
      const ugxAmount = isAnnual 
        ? (usdAnnual === 190 ? 725000 : usdAnnual === 490 ? 1800000 : 3650000) 
        : (usdMonthly === 19 ? 72500 : usdMonthly === 49 ? 180000 : 365000);
      return { 
        priceStr: `${ugxAmount.toLocaleString()} UGX/${isAnnual ? 'yr' : 'mo'}`, 
        amount: ugxAmount, 
        note: isAnnual ? 'Paid annually' : 'Paid monthly' 
      };
    } else if (selectedCurrency === 'NGN') {
      const ngnAmount = isAnnual 
        ? (usdAnnual === 190 ? 280000 : usdAnnual === 490 ? 720000 : 1450000) 
        : (usdMonthly === 19 ? 28000 : usdMonthly === 49 ? 72000 : 145000);
      return { 
        priceStr: `₦${ngnAmount.toLocaleString()}/${isAnnual ? 'yr' : 'mo'}`, 
        amount: ngnAmount, 
        note: isAnnual ? 'Paid annually' : 'Paid monthly' 
      };
    } else { // KES
      const kesAmount = isAnnual 
        ? (usdAnnual === 190 ? 25000 : usdAnnual === 490 ? 65000 : 130000) 
        : (usdMonthly === 19 ? 2500 : usdMonthly === 49 ? 6500 : 13000);
      return { 
        priceStr: `KSh ${kesAmount.toLocaleString()}/${isAnnual ? 'yr' : 'mo'}`, 
        amount: kesAmount, 
        note: isAnnual ? 'Paid annually' : 'Paid monthly' 
      };
    }
  };

  const individualPlans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0/mo',
      monthlyUsd: 0,
      annualUsd: 0,
      hasAnnual: false,
      hasCustom: false,
      icon: Box,
      description: 'Frictionless entry & viral discovery. Build initial business ideas, test local markets, & understand options.',
      features: [
        '2 feasibility studies in Launchpad',
        '1 Scrutiny Deck gap assessment',
        '1 Basic Discovery scan (limited)',
        '5 Dynamic Map Views & 2 Geocodes / mo',
        'NO PDF Report Exports (watermarked)',
        'Read-only War Room access (no saves)',
        'Full Hub visibility with feature previews'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      monthlyUsd: 19,
      annualUsd: 190,
      hasAnnual: true,
      hasCustom: false,
      icon: Crown,
      tag: 'Most Popular',
      description: 'The definitive suite for serious founders seeking structured intelligence and full feasibility output.',
      features: [
        'Unlimited intelligence searches in Launchpad',
        'Unlimited scans in Discovery Hub',
        '80 Dynamic Map Views & 20 Geocodes / mo',
        'Full PDF report exports (no watermarks)',
        'Priority server queuing for Gemini jobs',
        'Unlimited War Room saves and comparisons',
        'Email support (24-hour turnaround)'
      ]
    },
    {
      id: 'optimize',
      name: 'Optimize',
      monthlyUsd: 49,
      annualUsd: 490,
      hasAnnual: true,
      hasCustom: false,
      icon: Gauge,
      description: 'The master suite for operational scale, performance benchmarking, and active portfolio management.',
      features: [
        'Everything in Premium Tier',
        '250 Dynamic Maps & 50 Geocodes / mo',
        '50 Distance Matrix Proximity Elements',
        'Full Operational Health Check',
        'GrowthMath Modeling Engine',
        'Interactive Strategy Sandbox',
        'Live Ops Dashboard',
        'Living SWOT with status sync',
        'Expansion Scout & Exit Architect',
        '1 connected live business data source',
        '5 team member seats included'
      ]
    }
  ];

  const corporatePlans = [
    {
      id: 'team',
      name: 'Team',
      monthlyUsd: 99,
      annualUsd: 990,
      hasAnnual: true,
      hasCustom: false,
      icon: Users,
      tag: 'Mid-Market Standard',
      description: 'Unified coordination, team-wide insights, secure stakeholders, and ERP/CRM integration pipelines.',
      features: [
        'Everything in Optimize Tier',
        '3,000 Pooled Dynamic Maps / mo',
        '300 Geocodes & 500 Distance Matrix',
        '5 full seat allocations by default',
        'Multi-user collaboration with custom RBAC',
        'Secure stakeholder link sharing',
        'Salesforce & HubSpot CRM integration',
        'ERP synchronicity (API synced variables)',
        'Localized international datasets',
        'Team annotations & action items on SWOT',
        'SLA priority email support (4-hour pledge)'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      monthlyUsd: 0,
      annualUsd: 0,
      hasCustom: true,
      icon: Building2,
      description: 'Tailored deployment, global scale, customized analytics modules, and direct executive oversight.',
      features: [
        'Everything in Team Tier',
        'Unlimited Dynamic Maps & Geocoding',
        'Unlimited Distance Matrix & Directions',
        'Unlimited team seat allocation',
        'White-label reports with branded PDFs & custom domain',
        'On-premise deployment or dedicated Cloud Run',
        'Dedicated Customer Success Manager (CSM)',
        'Quarterly business reviews & custom sector modeling',
        'Bulk API access with custom limit rules',
        'Guaranteed uptime & latency SLA agreements',
        'Advanced offline deployment options for field work',
        'Full government compliance audit logs'
      ]
    }
  ];

  const plans = activeTab === 'individual' ? individualPlans : corporatePlans;

  const handleSelectPlan = (plan: any) => {
    if (plan.id === 'free') {
      handleDowngradeToFree();
      return;
    }
    setCheckoutPlan(plan);
    setPaymentError(null);
    setCheckoutStep('idle');
  };

  const handleDowngradeToFree = async () => {
    try {
      if (effectiveUserId) {
        const userRef = doc(db, 'users', effectiveUserId);
        await updateDoc(userRef, {
          subscriptionTier: 'free',
          'subscription.tier': 'free',
          'subscription.status': 'active',
          'subscription.updatedAt': new Date().toISOString()
        });
        if (userProfile) {
          setUserProfile({
            ...userProfile,
            subscriptionTier: 'free'
          });
        }
      }
      onClose();
    } catch (e: any) {
      setPaymentError('Failed to update plan: ' + e.message);
    }
  };

  const handleInitiateDodoCheckout = async () => {
    if (!checkoutPlan) return;
    if (!isAuthenticated) {
      setIsSignInModalOpen(true);
      return;
    }

    setPaymentError(null);
    setCheckoutStep('creating-session');

    try {
      let token = '';
      if (auth.currentUser) {
        token = await auth.currentUser.getIdToken();
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          tier: checkoutPlan.id,
          billingCycle: billingCycle,
          provider: 'dodo',
          currency: selectedCurrency,
          email: effectiveEmail || 'billing@envesta.ai'
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success || !data.checkoutUrl) {
        throw new Error(data.error || data.message || 'Failed to initialize Dodo Payments checkout session');
      }

      setCheckoutUrl(data.checkoutUrl);
      setCheckoutStep('redirecting');

      // SAME-TAB REDIRECT
      window.location.href = data.checkoutUrl;

    } catch (err: any) {
      setCheckoutStep('idle');
      setPaymentError(err.message || 'Payment initiation failed. Please verify connection and try again.');
    }
  };

  const handleDownloadOfflineConfig = () => {
    if (!enterpriseBundle) return;
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(enterpriseBundle, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `envesta_offline_config_${companyName.toLowerCase().replace(/[^a-z0-9]/g, '_')}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const handleEnterpriseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnterpriseStep('submitting');
    setPaymentError(null);

    try {
      const resp = await fetch('/api/dispatch-enterprise-sla', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName,
          seatsNeeded,
          operationalJurisdiction: jurisdiction,
          userId: effectiveUserId || 'unknown'
        })
      });

      if (!resp.ok) {
        throw new Error('Failed to dispatch enterprise SLA contract request');
      }

      const bundle = await resp.json();
      setEnterpriseBundle(bundle.provision_bundle || bundle);
      setEnterpriseStep('success');

    } catch (err: any) {
      setEnterpriseStep('idle');
      setPaymentError(err.message || 'Error processing enterprise inquiry');
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 animate-in fade-in duration-200" id="envesta-upgrade-portal">
      <div 
        className="w-full max-w-5xl bg-[#322D29] border border-white/[0.06] overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[92vh] rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        id="upgrade-container"
      >
        {/* Header */}
        <header className="px-6 sm:px-8 py-5 border-b border-white/[0.06] flex items-center justify-between bg-[#322D29] shrink-0">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-[0.25em] font-heading">eNvesta AI</span>
            <h2 className="text-xl font-bold tracking-tight text-white font-heading leading-none mt-1">UPGRADE</h2>
          </div>
          <button 
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center text-white/60 hover:text-white bg-[#3a3430] border border-white/[0.06] hover:border-white/10 transition-colors cursor-pointer rounded-lg"
            aria-label="Close Upgrade Portal"
            id="upgrade-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Workspace Panels */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 sm:p-8 bg-[#322D29]">
          
          {!isAuthenticated ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-xl mx-auto py-8 text-center space-y-6"
            >
              <div className="w-16 h-16 bg-[#3a3430] border border-white/[0.06] rounded-xl flex items-center justify-center mx-auto text-white">
                <Lock className="w-8 h-8 text-[#F26522]" />
              </div>

              <div className="space-y-2">
                <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-[0.3em] font-heading">
                  Authentication Protocol Required
                </span>
                <h3 className="text-2xl font-bold uppercase text-white tracking-tight font-heading">
                  Sign In to Select Plan
                </h3>
                <p className="text-xs font-medium text-white/60 tracking-wide max-w-md mx-auto leading-relaxed">
                  Subscription capacity, enterprise API allocations, and offline SLA credentials must attach directly to an authenticated operative identity.
                </p>
              </div>

              <div className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-4 text-left max-w-md mx-auto">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-4 h-4 text-[#F26522] shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-bold uppercase text-white tracking-wider block">Encrypted Capacity Attribution</span>
                    <span className="text-[9px] font-medium text-white/60 block">Your billing receipts, active maps quotas, and CRM webhooks sync directly to your secure account.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-4 h-4 text-[#F26522] shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-bold uppercase text-white tracking-wider block">Instant Activation</span>
                    <span className="text-[9px] font-medium text-white/60 block">Immediate capability upgrade and access across all your devices.</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto pt-2">
                <button
                  onClick={() => {
                    onClose();
                    setIsSignInModalOpen(true);
                  }}
                  className="py-4 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase text-[10px] tracking-[0.2em] transition-all flex items-center justify-center gap-2 cursor-pointer rounded-lg"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Sign In</span>
                </button>
                <button
                  onClick={() => {
                    onClose();
                    setIsSignUpModalOpen(true);
                  }}
                  className="py-4 bg-[#3a3430] hover:bg-white/10 text-white border border-white/[0.08] font-bold uppercase text-[10px] tracking-[0.2em] transition-all flex items-center justify-center gap-2 cursor-pointer rounded-lg"
                >
                  <ShieldCheck className="w-4 h-4 text-[#F26522]" />
                  <span>Create Account</span>
                </button>
              </div>
            </motion.div>
          ) : (
            <AnimatePresence mode="wait">
              {!checkoutPlan ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="space-y-6"
                  key="plans-list-view"
                >
                {/* Control Bar */}
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-[#3a3430] p-4 border border-white/[0.06] rounded-xl">
                  <div className="flex border border-white/[0.06] p-1 bg-[#322D29] rounded-lg">
                    <button
                      onClick={() => setActiveTab('individual')}
                      className={`px-4 py-2 font-bold uppercase text-[9px] tracking-wider transition-all rounded-md cursor-pointer ${activeTab === 'individual' ? 'bg-[#3a3430] text-white border border-white/[0.08] shadow-sm' : 'text-white/40 hover:text-white/80'}`}
                    >
                      Individual Plans
                    </button>
                    <button
                      onClick={() => setActiveTab('corporate')}
                      className={`px-4 py-2 font-bold uppercase text-[9px] tracking-wider transition-all rounded-md cursor-pointer ${activeTab === 'corporate' ? 'bg-[#3a3430] text-white border border-white/[0.08] shadow-sm' : 'text-white/40 hover:text-white/80'}`}
                    >
                      Corporate / Company Tiers
                    </button>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] uppercase font-bold tracking-wider ${billingCycle === 'monthly' ? 'text-white' : 'text-white/40'}`}>Monthly</span>
                    <button 
                      onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'annual' : 'monthly')}
                      className="w-12 h-6 border border-white/10 bg-[#322D29] rounded-full relative flex items-center transition-all px-0.5 cursor-pointer"
                      aria-label="Toggle Billing Cycle"
                    >
                      <div className={`w-5 h-5 bg-[#F26522] rounded-full transition-transform ${billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-0'}`}></div>
                    </button>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] uppercase font-bold tracking-wider ${billingCycle === 'annual' ? 'text-white' : 'text-white/40'}`}>Annual</span>
                      <span className="bg-[#F26522] text-white text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">2 Months Free</span>
                    </div>
                  </div>

                  <div className="flex bg-[#322D29] p-1 border border-white/[0.06] rounded-lg">
                    {CURRENCIES.map((curr) => (
                      <button
                        key={`curr-toggle-${curr.code}`}
                        onClick={() => setSelectedCurrency(curr.code as any)}
                        className={`px-3 py-1.5 font-bold text-[9px] uppercase tracking-wider rounded-md transition-all cursor-pointer ${selectedCurrency === curr.code ? 'bg-[#3a3430] text-[#F26522] border border-white/[0.06]' : 'text-white/50 hover:text-white'}`}
                      >
                        {curr.code}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Grid of Plans */}
                <div className={`grid gap-6 ${plans.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto'}`}>
                  {plans.map((p) => {
                    const priceInfo = getDisplayPrice(p.monthlyUsd, p.annualUsd, p.hasAnnual);
                    const isCurrent = userProfile?.subscriptionTier === p.id;
                    const IconComponent = p.icon;

                    return (
                      <div 
                        key={`plan-card-${p.id}`} 
                        className={`bg-[#3a3430] border p-6 flex flex-col justify-between transition-all relative overflow-hidden group rounded-xl ${
                          isCurrent 
                            ? 'border-2 border-[#F26522]' 
                            : p.tag 
                            ? 'border-[#F26522]/40 hover:border-[#F26522]' 
                            : 'border-white/[0.06] hover:border-white/[0.14]'
                        }`}
                      >
                        {p.tag && (
                          <div className="absolute top-0 right-0 bg-[#F26522] text-white text-[8px] font-bold uppercase tracking-widest px-3 py-1 font-heading rounded-bl-lg">
                            {p.tag}
                          </div>
                        )}
                        {isCurrent && (
                          <div className="absolute top-0 left-0 bg-emerald-600 text-white text-[8px] font-bold uppercase tracking-widest px-3 py-0.5 font-heading rounded-br-lg">
                            Active Plan
                          </div>
                        )}

                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 border border-white/[0.08] bg-[#322D29] rounded-lg flex items-center justify-center">
                              <IconComponent className={`w-5 h-5 ${isCurrent || p.tag ? 'text-[#F26522]' : 'text-white/80'}`} />
                            </div>
                            <div>
                              <h3 className="text-base font-bold uppercase text-white tracking-tight font-heading">{p.name}</h3>
                              {p.hasCustom ? (
                                <p className="text-xl font-bold text-[#F26522] font-heading">Custom Pricing</p>
                              ) : (
                                <p className="text-xl font-bold text-[#F26522] font-heading">{priceInfo.priceStr}</p>
                              )}
                            </div>
                          </div>

                          <p className="text-[10px] font-medium text-white/60 tracking-wide border-l-2 border-[#F26522]/40 pl-3 py-1 italic leading-relaxed">
                            {p.description}
                          </p>

                          <div className="h-px w-full my-3 bg-white/[0.06]" />

                          <ul className="space-y-2 py-1">
                            {p.features.map((feature, idx) => (
                              <li key={`feat-${p.id}-${idx}`} className="flex items-start gap-2 text-[10px] font-medium uppercase tracking-wider text-white/80 leading-tight">
                                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="mt-8 pt-4 border-t border-white/[0.06]">
                          {isCurrent ? (
                            <button 
                              disabled
                              className="w-full py-4 bg-[#322D29] text-white/40 font-bold uppercase text-[9px] tracking-[0.2em] border border-white/[0.06] cursor-default text-center rounded-lg"
                            >
                              Current Provision
                            </button>
                          ) : p.hasCustom ? (
                            <button
                              onClick={() => handleSelectPlan(p)}
                              className="w-full py-4 bg-[#322D29] hover:bg-[#F26522] text-white font-bold uppercase text-[9px] tracking-[0.2em] border border-white/[0.08] hover:border-[#F26522] transition-all active:scale-[0.98] text-center cursor-pointer flex items-center justify-center gap-2 rounded-lg"
                            >
                              <span>Initiate Consultation</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleSelectPlan(p)}
                              className="w-full py-4 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase text-[9px] tracking-[0.2em] transition-all active:scale-[0.98] text-center cursor-pointer flex items-center justify-center gap-2 rounded-lg"
                            >
                              <span>Acquire {p.name} Capability</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="max-w-xl mx-auto bg-[#3a3430] border border-white/[0.06] rounded-xl p-8 space-y-6 relative shadow-2xl"
                key="checkout-flow-view"
              >
                <div className="absolute top-4 right-4 text-[10px] font-bold text-white/60 uppercase tracking-widest border border-white/10 px-3 py-1 bg-[#322D29] rounded-md flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#F26522]" /> Secure Checkout
                </div>

                <div className="flex items-center gap-3 border-b border-white/[0.06] pb-4">
                  <button 
                    onClick={() => {
                      setCheckoutPlan(null);
                      setPaymentError(null);
                      setCheckoutStep('idle');
                    }}
                    className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white border border-white/10 hover:bg-white/5 cursor-pointer rounded-lg"
                  >
                    <ArrowRight className="w-4 h-4 rotate-180" />
                  </button>
                  <div>
                    <span className="text-[9px] font-bold uppercase text-[#F26522] tracking-wider">Plan Selected</span>
                    <h3 className="text-lg font-bold uppercase text-white tracking-tight font-heading">{checkoutPlan.name} Tier Provision</h3>
                  </div>
                </div>

                {paymentError && (
                  <div className="p-4 bg-red-950/40 border border-red-500/30 rounded-lg flex items-center gap-3 animate-in fade-in">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-red-300 leading-tight">{paymentError}</p>
                  </div>
                )}

                {checkoutPlan.hasCustom ? (
                  <div className="space-y-4">
                    {enterpriseStep === 'submitting' && (
                      <div className="py-12 flex flex-col items-center justify-center text-center space-y-4 animate-pulse">
                        <div className="w-10 h-10 border-3 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white">DISPATCHING SECURE SLA CONTRACTS...</p>
                          <p className="text-[8px] font-medium text-white/40 uppercase tracking-widest mt-1">COMPILING ENTERPRISE OFFLINE LICENSE BUNDLES</p>
                        </div>
                      </div>
                    )}

                    {enterpriseStep === 'success' && (
                      <div className="p-6 bg-[#322D29] border border-white/[0.06] rounded-xl space-y-4 animate-in fade-in duration-300">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                            <Check className="w-4 h-4" />
                          </div>
                          <div>
                            <span className="text-[8px] font-bold uppercase tracking-widest text-emerald-400 block leading-none">SLA CLEARANCE GRANTED</span>
                            <span className="text-xs font-bold uppercase tracking-tighter text-white mt-1 block font-heading">OFF-GRID INFRASTRUCTURE PROVISIONED</span>
                          </div>
                        </div>

                        <div className="p-4 bg-[#3a3430] border border-white/[0.06] rounded-lg space-y-3">
                          <div className="flex justify-between items-center text-[9px] font-bold uppercase tracking-wider text-white/40">
                            <span>SLA Licence Key</span>
                            <span className="text-[#F26522] font-mono text-[10px]">{enterpriseBundle?.license_lease_id}</span>
                          </div>
                          <div className="flex justify-between items-center text-[9px] font-bold uppercase tracking-wider text-white/40">
                            <span>Assigned Seats</span>
                            <span className="text-white font-mono text-[10px]">{enterpriseBundle?.seat_count} Active Keys</span>
                          </div>
                          <div className="flex justify-between items-center text-[9px] font-bold uppercase tracking-wider text-white/40">
                            <span>Preset Region</span>
                            <span className="text-white font-mono text-[10px]">{enterpriseBundle?.offline_region_preset_coordinates?.center_loc}</span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <span className="text-[8px] font-bold uppercase tracking-widest text-white/40 block">Offline Initialization Instructions:</span>
                          <div className="p-3 bg-[#221e1b] text-white/70 font-mono text-[9px] border border-white/10 rounded-lg select-all overflow-x-auto">
                            # Pull local airgapped image:<br />
                            docker pull gcr.io/envesta-ai/offline-client:stable<br />
                            # Start with compile bundle:<br />
                            envesta-init --config=./envesta_offline_config.json
                          </div>
                        </div>

                        <div className="flex gap-3 pt-2">
                          <button
                            type="button"
                            onClick={handleDownloadOfflineConfig}
                            className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-[9px] tracking-widest transition-colors flex items-center justify-center gap-2 cursor-pointer rounded-lg"
                          >
                            Download Config
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setCheckoutPlan(null);
                              setEnterpriseStep('idle');
                              onClose();
                              window.location.reload();
                            }}
                            className="flex-1 py-3 bg-[#322D29] hover:bg-[#F26522] text-white font-bold uppercase text-[9px] tracking-widest transition-colors cursor-pointer border border-white/10 rounded-lg"
                          >
                            Complete SLA Setup
                          </button>
                        </div>
                      </div>
                    )}

                    {enterpriseStep === 'idle' && (
                      <>
                        <p className="text-[10px] font-medium text-white/60 tracking-wide leading-relaxed">
                          Enterprise integration and custom contracts are coordinated via direct account officer engagement. Please submit parameters below.
                        </p>
                        <form onSubmit={handleEnterpriseSubmit} className="space-y-4">
                          <div className="space-y-1">
                            <label className="text-[9px] font-bold text-white/80 uppercase tracking-widest">Company Organization Name</label>
                            <input 
                              required 
                              type="text" 
                              value={companyName}
                              onChange={(e) => setCompanyName(e.target.value)}
                              placeholder="e.g. Acme Africa Ltd" 
                              className="w-full text-[10px] font-bold uppercase p-3 bg-[#322D29] border border-white/10 text-white placeholder-white/30 focus:border-[#F26522] focus:outline-none rounded-lg" 
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-bold text-white/80 uppercase tracking-widest">Corporate Seat Allocation Needed</label>
                            <input 
                              required 
                              type="number" 
                              min="5" 
                              value={seatsNeeded}
                              onChange={(e) => setSeatsNeeded(e.target.value)}
                              placeholder="e.g. 50 seats" 
                              className="w-full text-[10px] font-bold uppercase p-3 bg-[#322D29] border border-white/10 text-white placeholder-white/30 focus:border-[#F26522] focus:outline-none rounded-lg" 
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-bold text-white/80 uppercase tracking-widest">Primary Operational Jurisdiction</label>
                            <input 
                              required 
                              type="text" 
                              value={jurisdiction}
                              onChange={(e) => setJurisdiction(e.target.value)}
                              placeholder="e.g. Uganda, Kenya, Nigeria" 
                              className="w-full text-[10px] font-bold uppercase p-3 bg-[#322D29] border border-white/10 text-white placeholder-white/30 focus:border-[#F26522] focus:outline-none rounded-lg" 
                            />
                          </div>
                          <button type="submit" className="w-full py-4 bg-[#F26522] text-white font-bold uppercase text-[10px] tracking-[0.2em] hover:bg-[#F26522]/90 transition-all cursor-pointer rounded-lg">
                            Dispatch Enterprise SLA Request & Deploy
                          </button>
                        </form>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Order Summary */}
                    <div className="bg-[#322D29] p-4 border border-white/[0.06] rounded-xl space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-[9px] font-bold uppercase text-white/40 tracking-widest">Selected Tier</span>
                        <span className="text-sm font-bold uppercase text-white font-heading">{checkoutPlan.name} Plan</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[9px] font-bold uppercase text-white/40 tracking-widest">Billing Interval</span>
                        <span className="text-[10px] font-medium uppercase text-white/80">{billingCycle === 'annual' ? 'Annual (2 Months Free)' : 'Monthly'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[9px] font-bold uppercase text-white/40 tracking-widest">Subscriber Account</span>
                        <span className="text-[10px] font-medium text-white/80">{effectiveEmail}</span>
                      </div>
                      <div className="pt-2 border-t border-white/[0.06] flex justify-between items-center">
                        <span className="text-[9px] font-bold uppercase text-white tracking-widest">Total Charging Amount</span>
                        <span className="text-xl font-bold text-[#F26522] font-heading">
                          {getDisplayPrice(checkoutPlan.monthlyUsd, checkoutPlan.annualUsd, checkoutPlan.hasAnnual).priceStr}
                        </span>
                      </div>
                    </div>

                    {/* Action States */}
                    {checkoutStep === 'idle' && (
                      <button 
                        onClick={handleInitiateDodoCheckout}
                        className="w-full py-4 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase text-[10px] tracking-[0.2em] transition-all cursor-pointer flex items-center justify-center gap-2 rounded-lg"
                      >
                        <Lock className="w-3.5 h-3.5" />
                        <span>Proceed to Secure Checkout</span>
                      </button>
                    )}

                    {checkoutStep === 'creating-session' && (
                      <div className="py-8 flex flex-col items-center justify-center space-y-3">
                        <div className="w-9 h-9 border-3 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-white animate-pulse text-center">
                          INITIALIZING SECURE CHECKOUT...
                        </p>
                      </div>
                    )}

                    {checkoutStep === 'awaiting-completion' && (
                      <div className="space-y-4 py-4 text-center">
                        <div className="w-12 h-12 bg-[#F26522]/10 border border-[#F26522]/30 rounded-full flex items-center justify-center mx-auto text-[#F26522]">
                          <ExternalLink className="w-6 h-6 animate-pulse" />
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-sm font-bold text-white uppercase tracking-widest font-heading">Checkout Opened</h4>
                          <p className="text-[10px] font-medium text-white/60 tracking-wider max-w-sm mx-auto leading-relaxed">
                            Complete your payment in the secure window. Your subscription activates automatically upon clearance.
                          </p>
                        </div>

                        {checkoutUrl && (
                          <div className="flex flex-col sm:flex-row gap-2 pt-2">
                            <a
                              href={checkoutUrl}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="flex-1 py-3 bg-[#F26522] hover:bg-[#F26522]/90 text-white font-bold uppercase text-[9px] tracking-widest flex items-center justify-center gap-1.5 transition-colors rounded-lg"
                            >
                              <ExternalLink className="w-3.5 h-3.5" /> Re-open Checkout
                            </a>
                            <button
                              onClick={() => {
                                window.location.reload();
                              }}
                              className="flex-1 py-3 bg-[#322D29] hover:bg-[#3a3430] text-white border border-white/10 font-bold uppercase text-[9px] tracking-widest flex items-center justify-center gap-1.5 transition-colors cursor-pointer rounded-lg"
                            >
                              <RefreshCw className="w-3.5 h-3.5" /> I Have Completed Payment
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        )}

        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
